/*
 * Product-card sibling swatches.
 *
 * The destination card is rendered by Shopify through
 * templates/product.card.liquid, so product-card.liquid remains the single
 * source of truth for images, prices, badges, options, and quick view.
 */
(() => {
  'use strict';

  const cardCache = new Map();
  const requests = new WeakMap();

  const cardUrl = (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.delete('variant');
    url.searchParams.delete('section_id');
    url.searchParams.set('view', 'card');
    return url.toString();
  };

  const getRenderedCard = (productUrl) => {
    const url = cardUrl(productUrl);

    if (!cardCache.has(url)) {
      cardCache.set(
        url,
        fetch(url, {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Unable to render product card: ${response.status}`
              );
            }

            return response.text();
          })
          .then((text) => {
            const card = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('product-card');

            if (!card) {
              throw new Error(
                'Rendered product card was not found.'
              );
            }

            return card;
          })
          .catch((error) => {
            cardCache.delete(url);
            throw error;
          })
      );
    }

    return cardCache.get(url);
  };

  document.addEventListener(
    'pointerenter',
    (event) => {
      const swatch = event.target.closest?.(
        '[data-product-card-sibling]'
      );

      const url = swatch?.dataset.href;

      if (url) {
        getRenderedCard(url).catch(() => {});
      }
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const swatch = event.target.closest?.(
        '[data-product-card-sibling]'
      );

      if (!swatch) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const card =
        swatch.closest('product-card');

      const url =
        swatch.dataset.href;

      if (
        !card ||
        !url ||
        swatch.classList.contains('active')
      ) {
        return;
      }

      const token = Symbol(url);

      requests.set(card, token);

      card.classList.add(
        'is-sibling-loading'
      );

      card.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        const rendered =
          await getRenderedCard(url);

        if (
          requests.get(card) !== token ||
          !card.isConnected
        ) {
          return;
        }

        /*
        * Preserve the existing swatches because they were
        * rendered with the current collection context.
        *
        * This keeps Sale collection cards limited to sale
        * colors after an AJAX sibling change.
        */
        const currentSwatchContainer =
          card.querySelector(
            '.product-card-sibling-swatches--container'
          );

        const newCard =
          rendered.cloneNode(true);

        const newSwatchContainer =
          newCard.querySelector(
            '.product-card-sibling-swatches--container'
          );

        if (
          currentSwatchContainer &&
          newSwatchContainer
        ) {
          const activeHandle =
            newCard.dataset
              .productCardHandle;

          /*
          * Update which preserved swatch is active.
          */
          currentSwatchContainer
            .querySelectorAll(
              '[data-product-card-sibling]'
            )
            .forEach((button) => {
              const isActive =
                button.dataset.handle ===
                activeHandle;

              button.classList.toggle(
                'active',
                isActive
              );

              button.setAttribute(
                'aria-pressed',
                String(isActive)
              );
            });

          newSwatchContainer.replaceWith(
            currentSwatchContainer
          );
        }

        card.replaceWith(newCard);
      } catch (error) {
        console.error(
          'Product-card sibling update failed:',
          error
        );

        if (card.isConnected) {
          card.classList.remove(
            'is-sibling-loading'
          );

          card.removeAttribute(
            'aria-busy'
          );
        }
      }
    }
  );

  const fitState = (button) => {
    const container = button.closest(
      '[data-product-card-fit-options]'
    );

    const script = container?.querySelector(
      '[data-product-card-fit-variants]'
    );

    if (!container || !script) {
      return null;
    }

    try {
      return {
        container,
        variants: JSON.parse(
          script.textContent
        ),
        waistIndex: Number(
          container.dataset.waistIndex
        ),
        inseamIndex: Number(
          container.dataset.inseamIndex
        )
      };
    } catch (error) {
      console.warn(
        'Could not parse product-card fit variants.',
        error
      );

      return null;
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const inseam = event.target.closest?.(
        '[data-fit-inseam]'
      );

      const waist = event.target.closest?.(
        '[data-fit-waist]'
      );

      if (!inseam && !waist) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const button =
        inseam || waist;

      if (button.disabled) {
        return;
      }

      const state =
        fitState(button);

      if (!state) {
        return;
      }

      if (inseam) {
        const selectedInseam =
          inseam.dataset.fitInseam;

        state.container.dataset.selectedInseam =
          selectedInseam;

        state.container
          .querySelectorAll(
            '[data-fit-inseam]'
          )
          .forEach((item) => {
            const active =
              item === inseam;

            item.classList.toggle(
              'is-active',
              active
            );

            item.setAttribute(
              'aria-pressed',
              String(active)
            );
          });

        state.container
          .querySelectorAll(
            '[data-fit-waist]'
          )
          .forEach((item) => {
            const variant =
              state.variants.find(
                (candidate) =>
                  candidate.available &&
                  String(
                    candidate.options[
                      state.waistIndex
                    ]
                  ) ===
                    item.dataset.fitWaist &&
                  String(
                    candidate.options[
                      state.inseamIndex
                    ]
                  ) === selectedInseam
              );

            item.disabled = !variant;

            item.classList.toggle(
              'is-disabled',
              !variant
            );

            item.dataset.variantId =
              variant?.id || '';

            item.setAttribute(
              'aria-disabled',
              String(!variant)
            );

            item.title =
              `${item.dataset.fitWaist}×${selectedInseam}`;
          });

        return;
      }

      const card =
        waist.closest('product-card');

      if (
        waist.dataset.variantId &&
        typeof card?.addToCart ===
          'function'
      ) {
        card.addToCart(
          waist.dataset.variantId,
          waist
        );
      }
    }
  );
})();

(() => {
  'use strict';

  let initialized = false;

  const all = (selector) =>
    document.querySelectorAll(selector);

  const each = (selector, callback) =>
    all(selector).forEach(callback);

  const show = (element) => {
    if (!element) return;

    element.hidden = false;
    element.style.removeProperty('display');

    if (getComputedStyle(element).display === 'none') {
      element.style.display = 'block';
    }
  };

  const hide = (element) => {
    if (element) {
      element.style.display = 'none';
    }
  };

  const showAll = (selector) =>
    each(selector, show);

  const hideAll = (selector) =>
    each(selector, hide);

  const addClass = (selector, className) =>
    each(selector, (element) =>
      element.classList.add(className)
    );

  const removeClass = (selector, className) =>
    each(selector, (element) =>
      element.classList.remove(className)
    );

  const setText = (selector, value) =>
    each(selector, (element) => {
      element.textContent = value;
    });

  const daysInMonth = (month) =>
    new Date(2021, month, 0).getDate();

  function initializeSwellRewards() {
    if (initialized) {
      return true;
    }

    if (
      !window.swellAPI ||
      typeof window.swellAPI.getCustomerDetails !==
        'function'
    ) {
      return false;
    }

    let customer;

    try {
      customer =
        window.swellAPI.getCustomerDetails();
    } catch (error) {
      return false;
    }

    if (
      !customer?.vipTier ||
      !customer.vipTierStats ||
      !customer.vipTierStatsNeeded
    ) {
      return false;
    }

    initialized = true;

    const tier = customer.vipTier.name;
    const amountSpent =
      customer.vipTierStats.amountSpentCents;

    const amountNeeded =
      customer.vipTierStatsNeeded.amountSpentCents;

    const maintainAmount =
      customer.vipTierStatsNeededMaintain
        ?.amountSpentCents || 0;

    let nextTierAmount;
    let currentTierMinimum;
    let nextTierProgress;
    let maintainProgress;

    if (tier === 'Tier 1') {
      nextTierAmount = 30000;
      currentTierMinimum = 100;
    } else if (tier === 'Tier 2') {
      nextTierAmount = 60000;
      currentTierMinimum = 30000;
    } else if (tier === 'Tier 3') {
      nextTierAmount = 100000;
      currentTierMinimum = 60000;
    } else if (tier === 'Ambassador') {
      nextTierAmount = 100000;
      currentTierMinimum = 100000;

      addClass('.swell-maintain', 'active-tab');
      show(
        document.querySelector(
          '#swell_maintain_tier'
        )
      );

      removeClass('.swell-next', 'active-tab');
      hide(
        document.querySelector(
          '#swell_next_tier'
        )
      );
    } else {
      nextTierAmount = 100;
      currentTierMinimum = 0;

      hideAll('.swell-maintain');
      hide(
        document.querySelector(
          '#swell_maintain_tier'
        )
      );
    }

    nextTierProgress =
      ((nextTierAmount - amountNeeded) /
        nextTierAmount) *
        100 +
      '%';

    if (currentTierMinimum > 0) {
      maintainProgress =
        ((currentTierMinimum - maintainAmount) /
          currentTierMinimum) *
          100 +
        '%';
    }

    each('.swell-current-tier', (element) => {
      const label =
        document.createElement('strong');

      label.textContent = 'CURRENT TIER: ';

      element.replaceChildren(
        label,
        document.createTextNode(
          tier.toUpperCase()
        )
      );
    });

    setText(
      '.swell-tier-min',
      '$' + currentTierMinimum / 100
    );

    setText(
      '.swell-next-tier',
      '$' + nextTierAmount / 100
    );

    setText(
      '.swell-amount-spent',
      '$' + amountSpent / 100
    );

    const progress =
      document.querySelector('#progress');

    const maintainProgressBar =
      document.querySelector(
        '#progress_maintain'
      );

    if (progress) {
      progress.style.width =
        nextTierProgress;
    }

    if (
      maintainProgressBar &&
      maintainProgress
    ) {
      maintainProgressBar.style.width =
        maintainProgress;
    }

    each('.progress_bar', (progressBar) => {
      progressBar.addEventListener(
        'mouseenter',
        () => {
          each(
            'span.swell-amount-needed-next',
            (element) => {
              element.innerHTML =
                '<span style="font-weight:700;">$' +
                amountNeeded / 100 +
                ' TO GO!</span>';
            }
          );

          each(
            '.swell-amount-needed-current',
            (element) => {
              element.innerHTML =
                '<span style="font-weight:700;">$' +
                maintainAmount / 100 +
                ' TO GO!</span>';
            }
          );
        }
      );

      progressBar.addEventListener(
        'mouseleave',
        () => {
          each(
            'span.swell-amount-needed-next',
            (element) => {
              element
                .querySelector(
                  'span:last-child'
                )
                ?.remove();
            }
          );

          each(
            '.swell-amount-needed-current',
            (element) => {
              element
                .querySelector(
                  'span:last-child'
                )
                ?.remove();
            }
          );
        }
      );
    });

    setText('.my-tier span', tier);

    const loader = document.querySelector(
      '#swell-reward-linksoul ' +
        '.loader-container'
    );

    if (loader) {
      const animation = loader.animate(
        [
          { opacity: 1 },
          { opacity: 0 }
        ],
        {
          duration: 600,
          easing: 'ease'
        }
      );

      animation.addEventListener(
        'finish',
        () => hide(loader),
        { once: true }
      );
    }

    each('.swell-maintain', (button) => {
      button.addEventListener(
        'click',
        () => {
          button.classList.add(
            'active-tab'
          );

          removeClass(
            '.swell-next',
            'active-tab'
          );

          const nextTier =
            document.querySelector(
              '#swell_next_tier'
            );

          const maintainTier =
            document.querySelector(
              '#swell_maintain_tier'
            );

          hide(nextTier);
          nextTier?.classList.remove(
            'active-tab'
          );

          show(maintainTier);
          maintainTier?.classList.add(
            'active-tab'
          );
        }
      );
    });

    each('.swell-next', (button) => {
      button.addEventListener(
        'click',
        () => {
          button.classList.add(
            'active-tab'
          );

          removeClass(
            '.swell-maintain',
            'active-tab'
          );

          const nextTier =
            document.querySelector(
              '#swell_next_tier'
            );

          const maintainTier =
            document.querySelector(
              '#swell_maintain_tier'
            );

          hide(maintainTier);
          maintainTier?.classList.remove(
            'active-tab'
          );

          show(nextTier);
        }
      );
    });

    if (
      Array.isArray(
        customer.actionHistoryItems
      )
    ) {
      showAll('.custom_history_wrapper');

      const tableBody =
        document.querySelector(
          '#custom_history_container ' +
            'table tbody'
        );

      customer.actionHistoryItems.forEach(
        (historyItem) => {
          if (!tableBody) return;

          const row =
            document.createElement('tr');

          const cells = [
            {
              headers:
                'RowOrder ColumnDate',
              label: 'Date',
              value: historyItem.date
            },
            {
              headers:
                'RowOrder ColumnAction',
              label: 'Action',
              value: historyItem.action
            },
            {
              headers:
                'RowOrder ColumnCredit',
              label: 'Credit',
              value:
                '$' + historyItem.credits
            },
            {
              headers:
                'RowOrder Status',
              label: 'Status',
              value: historyItem.status
            }
          ];

          cells.forEach((cellData) => {
            const cell =
              document.createElement('td');

            cell.setAttribute(
              'headers',
              cellData.headers
            );

            cell.dataset.label =
              cellData.label;

            cell.textContent =
              cellData.value ?? '';

            row.appendChild(cell);
          });

          tableBody.appendChild(row);
        }
      );
    }

    show(
      document.querySelector(
        '#swell-reward-linksoul'
      )
    );

    const monthSelect =
      document.querySelector('#bmonth');

    const daySelect =
      document.querySelector('#bday');

    monthSelect?.addEventListener(
      'change',
      () => {
        const month = monthSelect.value;

        if (!daySelect) return;

        daySelect.replaceChildren();

        if (!month) {
          daySelect.disabled = true;
          return;
        }

        daySelect.disabled = false;

        const totalDays =
          daysInMonth(month);

        for (
          let day = 1;
          day <= totalDays;
          day++
        ) {
          const option =
            document.createElement('option');

          option.value = day;
          option.textContent = day;

          daySelect.appendChild(option);
        }
      }
    );

    each(
      '.swell-birthday-btn',
      (button) => {
        button.addEventListener(
          'click',
          () => {
            if (
              !monthSelect ||
              !daySelect ||
              !window.Swell?.Customer
            ) {
              return;
            }

            window.Swell.Customer.saveBirthday(
              {
                month: monthSelect.value,
                day: daySelect.value,
                year: '2021'
              },
              () => {
                console.log(
                  'Birthday Success'
                );
              },
              () => {
                console.log(
                  'Birthday Error'
                );
              }
            );
          }
        );
      }
    );

    return true;
  }

  /*
   * Catch a native Swell event if available.
   */
  document.addEventListener(
    'swell:setup',
    initializeSwellRewards
  );

  /*
   * The legacy Swell SDK may trigger a jQuery-only
   * event, so briefly check for the API becoming ready.
   */
  if (!initializeSwellRewards()) {
    let attempts = 0;

    const swellTimer = window.setInterval(
      () => {
        attempts++;

        if (
          initializeSwellRewards() ||
          attempts >= 100
        ) {
          window.clearInterval(
            swellTimer
          );
        }
      },
      100
    );
  }
})();

/*
 * Preserve the currently selected options when switching sibling products,
 * AJAX-load the sibling, and update the relevant PDP elements.
 */
(() => {
  'use strict';

  const productCache =
    new Map();

  let activeRequestController =
    null;

  const normalize = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase();

  const isColorOption = (
    optionName
  ) => {
    const name =
      normalize(optionName);

    return (
      name === 'color' ||
      name === 'colour' ||
      name.includes('color') ||
      name.includes('colour')
    );
  };

  const getProductJsonUrl = (
    productUrl
  ) => {
    const url = new URL(
    productUrl,
    window.location.origin
  );

  url.searchParams.delete(
    'variant'
  );

  url.searchParams.delete(
    'section_id'
  );

  url.pathname =
    `${url.pathname.replace(/\/$/, '')}.js`;

  return url.toString();
};

/*
  * Cache Shopify product JSON so each product is only requested once.
  */
const getProductJson = (
  productUrl
) => {
  const requestUrl =
    getProductJsonUrl(
      productUrl
    );

  if (
    !productCache.has(
      requestUrl
    )
  ) {
    const request = fetch(
      requestUrl,
      {
        credentials:
          'same-origin',
        headers: {
          Accept:
            'application/json',
          'X-Requested-With':
            'XMLHttpRequest'
        }
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load product data: ${response.status}`
          );
        }

        return response.json();
      })
      .catch((error) => {
        productCache.delete(
          requestUrl
        );

        throw error;
      });

    productCache.set(
      requestUrl,
      request
    );
  }

  return productCache.get(
    requestUrl
  );
};

const getOptionNames = (
  product
) => {
  if (
    !Array.isArray(
      product.options
    )
  ) {
    return [];
  }

  return product.options.map(
    (option) =>
      typeof option === 'string'
        ? option
        : option?.name || ''
  );
};

const getVariantOptions = (
  variant
) => {
  if (
    Array.isArray(
      variant?.options
    )
  ) {
    return variant.options;
  }

  return [
    variant?.option1,
    variant?.option2,
    variant?.option3
  ].filter(
    (value) =>
      value != null
  );
};

const getProductInfo = (
  element
) => {
  return (
    element.closest(
      '[id^="ProductInfo-"]'
    ) ||
    element.closest(
      '.product-information--inner'
    )
  );
};

const getMainProductSection = (
  element
) => {
  return element.closest(
    '[id^="shopify-section-"]'
  );
};

const getSectionId = (
  section
) => {
  return (
    section?.id?.replace(
      'shopify-section-',
      ''
    ) || ''
  );
};

const getCurrentVariantId = (
  siblingsContainer,
  sectionId
) => {
  const productInfo =
    getProductInfo(
      siblingsContainer
    );

  const productInfoSectionId =
    productInfo?.id?.replace(
      'ProductInfo-',
      ''
    );

  const resolvedSectionId =
    productInfoSectionId ||
    sectionId;

  if (!resolvedSectionId) {
    return null;
  }

  const variantInput =
    document.querySelector(
      `#product-form-${CSS.escape(
        resolvedSectionId
      )} input[name="id"]`
    );

  return variantInput?.value
    ? String(
        variantInput.value
      )
    : null;
};

const getCurrentProductUrl = (
  siblingsContainer
) => {
  const productInfo =
    getProductInfo(
      siblingsContainer
    );

  const variantSelector =
    productInfo?.querySelector(
      'variant-selects[data-url], ' +
      'variant-radios[data-url]'
    );

  return (
    variantSelector
      ?.dataset.url ||
    window.location.pathname
  );
};

/*
 * Refresh product recommendations after the selected sibling changes.
 *
 * The recommendation block inside the main product section is copied from
 * the newly rendered sibling section. This supports both Shopify's
 * data-url-based recommendations and recommendations rendered directly from
 * a product metafield. The separate "You may also like" section is then
 * reloaded with the new sibling product ID.
 */
const updateProductRecommendations = ({
  currentSection,
  newSection,
  productId
}) => {
  const currentInlineRecommendations =
    currentSection?.querySelector(
      'product-recommendations'
    );

  const newInlineRecommendations =
    newSection?.querySelector(
      'product-recommendations'
    );

  if (
    currentInlineRecommendations &&
    newInlineRecommendations
  ) {
    currentInlineRecommendations.innerHTML =
      newInlineRecommendations.innerHTML;

    const newRecommendationsUrl =
      newInlineRecommendations.dataset.url;

    if (newRecommendationsUrl) {
      currentInlineRecommendations.dataset.url =
        newRecommendationsUrl;

      currentInlineRecommendations.classList.remove(
        'product-recommendations--loaded'
      );

      if (
        typeof currentInlineRecommendations
          .fetchProducts === 'function'
      ) {
        currentInlineRecommendations.fetchProducts();
      }
    } else {
      currentInlineRecommendations.removeAttribute(
        'data-url'
      );
    }
  } else if (currentInlineRecommendations) {
    currentInlineRecommendations.innerHTML = '';
    currentInlineRecommendations.removeAttribute(
      'data-url'
    );
  }

  const recommendations =
    document.querySelector(
      '.section-product-recommendations ' +
      'product-recommendations'
    );

  if (
    !productId ||
    !recommendations ||
    !recommendations.dataset.url ||
    typeof recommendations
      .fetchProducts !== 'function'
  ) {
    return;
  }

  const url = new URL(
    recommendations.dataset.url,
    window.location.origin
  );

  url.searchParams.set(
    'product_id',
    String(productId)
  );

  recommendations.dataset.url =
    url.toString();

  recommendations.innerHTML = '';

  recommendations.classList.remove(
    'product-recommendations--loaded'
  );

  recommendations.fetchProducts();
};

/*
  * Build a map of the currently selected non-color options.
  *
  * Example:
  * Size   => XL
  * Waist  => 34
  * Inseam => 32
  */
const getCurrentSelections =
  async (
    siblingsContainer,
    sectionId
  ) => {
    const currentProductUrl =
      getCurrentProductUrl(
        siblingsContainer
      );

    const currentVariantId =
      getCurrentVariantId(
        siblingsContainer,
        sectionId
      );

    const currentProduct =
      await getProductJson(
        currentProductUrl
      );

    let currentVariant =
      currentProduct
        .variants?.find(
          (variant) =>
            String(
              variant.id
            ) ===
            currentVariantId
        );

    if (!currentVariant) {
      currentVariant =
        currentProduct
          .variants?.find(
            (variant) =>
              variant.available
          ) ||
        currentProduct
          .variants?.[0];
    }

    if (!currentVariant) {
      throw new Error(
        'The current product has no variants.'
      );
    }

    const optionNames =
      getOptionNames(
        currentProduct
      );

    const optionValues =
      getVariantOptions(
        currentVariant
      );

    const selections =
      new Map();

    optionNames.forEach(
      (
        optionName,
        index
      ) => {
        if (
          !isColorOption(
            optionName
          ) &&
          optionValues[
            index
          ] != null
        ) {
          selections.set(
            normalize(
              optionName
            ),
            normalize(
              optionValues[
                index
              ]
            )
          );
        }
      }
    );

    return selections;
  };

const variantMatchesSelections = (
  variant,
  optionNames,
  currentSelections
) => {
  const variantValues =
    getVariantOptions(
      variant
    );

  return optionNames.every(
    (
      optionName,
      index
    ) => {
      if (
        isColorOption(
          optionName
        )
      ) {
        return true;
      }

      const normalizedName =
        normalize(
          optionName
        );

      /*
        * Ignore options that the two products do not share.
        */
      if (
        !currentSelections.has(
          normalizedName
        )
      ) {
        return true;
      }

      return (
        normalize(
          variantValues[
            index
          ]
        ) ===
        currentSelections.get(
          normalizedName
        )
      );
    }
  );
};

const findMatchingVariant = (
  product,
  currentSelections
) => {
  const optionNames =
    getOptionNames(
      product
    );

  const matches = (
    product.variants || []
  ).filter(
    (variant) =>
      variantMatchesSelections(
        variant,
        optionNames,
        currentSelections
      )
  );

  /*
    * Prefer an available match but preserve exact options
    * when the matching variant is sold out.
    */
  return (
    matches.find(
      (variant) =>
        variant.available
    ) ||
    matches[0] ||
    null
  );
};

const buildSiblingDestination =
  async (
    link,
    sectionId
  ) => {
    const siblingsContainer =
      link.closest(
        '.variations--siblings'
      );

    const siblingProductUrl =
      link.dataset
        .siblingProductUrl ||
      link.href;

    const siblingProduct =
      await getProductJson(
        siblingProductUrl
      );

    const destinationUrl =
      new URL(
        siblingProductUrl,
        window.location.origin
      );

    if (
      !siblingsContainer
    ) {
      return {
        url:
          destinationUrl
            .toString(),
        product:
          siblingProduct
      };
    }

    const currentSelections =
      await getCurrentSelections(
        siblingsContainer,
        sectionId
      );

    const matchingVariant =
      findMatchingVariant(
        siblingProduct,
        currentSelections
      );

    if (
      matchingVariant?.id
    ) {
      destinationUrl
        .searchParams
        .set(
          'variant',
          matchingVariant.id
        );
    } else {
      destinationUrl
        .searchParams
        .delete(
          'variant'
        );

      console.warn(
        'No matching sibling variant found.',
        {
          sibling:
            siblingProduct
              .handle,
          selections:
            Object.fromEntries(
              currentSelections
            )
        }
      );
    }

    return {
      url:
        destinationUrl
          .toString(),
      product:
        siblingProduct
    };
  };

const replaceOptionalElement = (
  currentElement,
  newElement
) => {
  if (
    currentElement &&
    newElement
  ) {
    currentElement.replaceWith(
      newElement
    );
  } else if (
    currentElement
  ) {
    currentElement.remove();
  }
};

/*
  * Reconnect the main and sticky selectors after AJAX replacement.
  */
const reconnectVariantSelectors = (
  sectionId
) => {
  const selectors =
    Array.from(
      document.querySelectorAll(
        'variant-selects'
      )
    ).filter(
      (selector) =>
        selector.isConnected &&
        selector.dataset
          .section ===
          sectionId
    );

  selectors.forEach(
    (selector) => {
      selector.other =
        selectors.filter(
          (
            otherSelector
          ) =>
            otherSelector !==
            selector
        );

      if (
        typeof selector
          .updateOptions ===
        'function'
      ) {
        selector.updateOptions();
      }

      if (
        typeof selector
          .updateMasterId ===
        'function'
      ) {
        selector.updateMasterId();
      }

      if (
        typeof selector
          .updateVariantText ===
        'function'
      ) {
        selector.updateVariantText();
      }

      if (
        typeof selector
          .setDisabled ===
        'function'
      ) {
        selector.setDisabled();
      }
    }
  );
};

const productIsOnSale = (
  product
) => {
  return (
    product?.variants || []
  ).some(
    (variant) =>
      variant
        .compare_at_price !=
        null &&
      Number(
        variant
          .compare_at_price
      ) >
        Number(
          variant.price
        )
  );
};

const updateFinalSaleMessage = (
  currentSection,
  product
) => {
  const message =
    currentSection
      .querySelector(
        '.final_sale'
      );

  if (!message) {
    return;
  }

  message.style.display =
    productIsOnSale(
      product
    )
      ? 'block'
      : 'none';
};

const updateProductImageCountClass = (
  currentSection,
  product
) => {
  const productRow =
    currentSection?.querySelector(
      '.thb-product-detail > .row.full-width-row'
    );

  if (!productRow) {
    return;
  }

  const imageCount =
    Array.isArray(product?.images)
      ? product.images.length
      : 0;

  productRow.classList.toggle(
    'one-image-width',
    imageCount === 1
  );
};


const updateProductFormVariant = (
  sectionId,
  destinationUrl
) => {
  const destination =
    new URL(
      destinationUrl,
      window.location.origin
    );

  const variantId =
    destination
      .searchParams
      .get('variant');

  if (!variantId) {
    return;
  }

  const variantInput =
    document.querySelector(
      `#product-form-${CSS.escape(
        sectionId
      )} input[name="id"]`
    );

  if (!variantInput) {
    return;
  }

  variantInput.value =
    variantId;

  variantInput.dispatchEvent(
    new Event(
      'change',
      {
        bubbles: true
      }
    )
  );
};

const waitForDOM = () => {
  return new Promise(
    (resolve) => {
      requestAnimationFrame(
        () => {
          requestAnimationFrame(
            resolve
          );
        }
      );
    }
  );
};

  /*
   * The theme adds this class when Quick View first opens.
   * Restore it after changing products inside an open drawer.
   */
  const activateQuickViewGallery = (
    drawerContent
  ) => {
    drawerContent
      .querySelector(
        '.product-quick-images--container'
      )
      ?.classList.add('active');
  };

  /*
  * Keep the Quick View details link synchronized with
  * Size, Waist, Inseam, and other non-color selections.
  *
  * This is attached to document because Product-Drawer may not
  * exist yet when custom-scripts.js initially runs.
  */
  /*
 * Update the Quick View details link after the theme has
 * resolved the actual selected variant.
 */
  document.addEventListener(
    'change',
    (event) => {
      const variantInput =
        event.target.closest?.(
          '#Product-Drawer ' +
          'form[data-type="add-to-cart-form"] ' +
          'input[name="id"]'
        );

      if (
        !variantInput ||
        !variantInput.value
      ) {
        return;
      }

      const drawer =
        variantInput.closest(
          '#Product-Drawer'
        );

      const variantSelector =
        drawer?.querySelector(
          '.thb-product-detail--quick-view ' +
          'variant-selects[data-url]'
        );

      const detailsLink =
        drawer?.querySelector(
          '.view-product-details'
        );

      if (!detailsLink) {
        return;
      }

      const detailsUrl =
        new URL(
          variantSelector?.dataset.url ||
            detailsLink.href,
          window.location.origin
        );

      detailsUrl.searchParams.delete(
        'view'
      );

      detailsUrl.searchParams.delete(
        'section_id'
      );

      detailsUrl.searchParams.set(
        'variant',
        variantInput.value
      );

      detailsLink.href =
        detailsUrl.pathname +
        detailsUrl.search;
    },
    true
  );

  /*
   * AJAX-load a sibling inside the side Quick View drawer.
   */
  const loadQuickViewSibling =
    async ({
      destinationUrl,
      currentDrawer,
      sectionId
    }) => {
      if (
        !currentDrawer ||
        !sectionId
      ) {
        window.location.assign(
          destinationUrl
        );

        return;
      }

      activeRequestController
        ?.abort();

      const requestController =
        new AbortController();

      activeRequestController =
        requestController;

      const currentDrawerContent =
        currentDrawer
          .querySelector(
            '#Product-Drawer-Content'
          );

      if (
        !currentDrawerContent
      ) {
        window.location.assign(
          destinationUrl
        );

        return;
      }

      currentDrawer
        .classList.add(
          'is-loading'
        );

      document.body
        .classList.add(
          'sibling-product-loading'
        );        

      currentDrawer.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        const requestUrl =
          new URL(
            destinationUrl,
            window.location.origin
          );

        requestUrl
          .searchParams
          .set(
            'view',
            'quick-view'
          );

        const response =
          await fetch(
            requestUrl.toString(),
            {
              credentials:
                'same-origin',
              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              },
              signal:
                requestController
                  .signal
            }
          );

        if (!response.ok) {
          throw new Error(
            `Unable to load quick-view sibling: ${response.status}`
          );
        }

        const responseDocument =
          new DOMParser()
            .parseFromString(
              await response.text(),
              'text/html'
            );

        const newDrawerContent =
          responseDocument
            .querySelector(
              '#Product-Drawer-Content'
            );

        if (
          !newDrawerContent
        ) {
          throw new Error(
            'The destination quick-view content was not found.'
          );
        }

        /*
         * This replaces images, title, price, sibling colors,
         * variants, Add to Cart form, description, and details link.
         */
        currentDrawerContent
          .replaceChildren(
            ...Array.from(
              newDrawerContent
                .childNodes
            )
          );

        activateQuickViewGallery(
          currentDrawerContent
        );

        const productDetailsLink =
          currentDrawerContent
            .querySelector(
              '.view-product-details'
            );

        if (
          productDetailsLink
        ) {
          const productDetailsUrl =
            new URL(
              destinationUrl,
              window.location.origin
            );

          productDetailsUrl
            .searchParams
            .delete('view');

          productDetailsUrl
            .searchParams
            .delete(
              'section_id'
            );

          productDetailsLink.href =
            productDetailsUrl
              .pathname +
            productDetailsUrl
              .search;
        }

        await waitForDOM();

        activateQuickViewGallery(
          currentDrawerContent
        );

        const newVariantSelector =
          currentDrawerContent
            .querySelector(
              'variant-selects[data-section]'
            );

        const newSectionId =
          newVariantSelector
            ?.dataset.section ||
          sectionId;

        reconnectVariantSelectors(
          newSectionId
        );

        updateProductFormVariant(
          newSectionId,
          destinationUrl
        );

        if (
          typeof Shopify !==
            'undefined' &&
          Shopify.PaymentButton
        ) {
          Shopify
            .PaymentButton
            .init();
        }

        if (
          window.ProductModel
        ) {
          window.ProductModel
            .loadShopifyXR();
        }

        currentDrawerContent
          .scrollTo({
            top: 0,
            behavior: 'smooth'
          });

        document.dispatchEvent(
          new CustomEvent(
            'product:quick-view-sibling-loaded',
            {
              detail: {
                url:
                  destinationUrl,
                sectionId:
                  newSectionId,
                container:
                  currentDrawerContent
              }
            }
          )
        );
      } catch (error) {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        console.warn(
          'Could not AJAX-load the quick-view sibling.',
          error
        );

        window.location.assign(
          destinationUrl
        );
      } finally {
        if (
          activeRequestController ===
          requestController
        ) {
          activeRequestController =
            null;

          currentDrawer
            .classList.remove(
              'is-loading'
            );

          currentDrawer
            .removeAttribute(
              'aria-busy'
            );

          document.body
            .classList.remove(
              'sibling-product-loading'
            );            
        }
      }
    };

  /*
   * AJAX-load a sibling on the full product page.
   */
  const loadSiblingProduct =
    async ({
      destinationUrl,
      siblingProduct = null,
      currentSection,
      addHistoryEntry = true
    }) => {
      if (!currentSection) {
        window.location.assign(
          destinationUrl
        );

        return;
      }

      const sectionId =
        getSectionId(
          currentSection
        );

      if (!sectionId) {
        window.location.assign(
          destinationUrl
        );

        return;
      }

      activeRequestController
        ?.abort();

      const requestController =
        new AbortController();

      activeRequestController =
        requestController;

      currentSection
        .classList.add(
          'is-loading'
        );

      document.body
        .classList.add(
          'sibling-product-loading'
        );

      currentSection
        .setAttribute(
          'aria-busy',
          'true'
        );

      try {
        const requestUrl =
          new URL(
            destinationUrl,
            window.location.origin
          );

        requestUrl
          .searchParams
          .set(
            'section_id',
            sectionId
          );

        const productPromise =
          siblingProduct
            ? Promise.resolve(
                siblingProduct
              )
            : getProductJson(
                destinationUrl
              );

        const [
          response,
          destinationProduct
        ] = await Promise.all([
          fetch(
            requestUrl.toString(),
            {
              credentials:
                'same-origin',
              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              },
              signal:
                requestController
                  .signal
            }
          ),
          productPromise
        ]);

        if (!response.ok) {
          throw new Error(
            `Unable to load sibling product: ${response.status}`
          );
        }

        const responseDocument =
          new DOMParser()
            .parseFromString(
              await response.text(),
              'text/html'
            );

        const newSection =
          responseDocument
            .getElementById(
              `shopify-section-${sectionId}`
            );

        const newGrid =
          newSection
            ?.querySelector(
              '.product-grid-container'
            );

        if (
          !newSection ||
          !newGrid
        ) {
          throw new Error(
            'The destination product grid was not found.'
          );
        }

        const escapedSectionId =
          CSS.escape(
            sectionId
          );

        const currentImageContainer =
          currentSection
            .querySelector(
              '.product-image-container'
            );

        const newImageContainer =
          newSection
            .querySelector(
              '.product-image-container'
            );

        const currentVariantSelects =
          currentSection
            .querySelector(
              `#variant-selects-${escapedSectionId}`
            );

        const newVariantSelects =
          newSection
            .querySelector(
              `#variant-selects-${escapedSectionId}`
            );
                    if (
          !currentImageContainer ||
          !newImageContainer
        ) {
          throw new Error(
            'The destination product image container was not found.'
          );
        }

        if (
          !currentVariantSelects ||
          !newVariantSelects
        ) {
          throw new Error(
            'The destination product variant selector was not found.'
          );
        }

        const currentSiblings =
          currentSection
            .querySelector(
              '.variations--siblings'
            );

        const newSiblings =
          newSection
            .querySelector(
              '.variations--siblings'
            );

        const currentPrice =
          currentSection
            .querySelector(
              `#price-${escapedSectionId}`
            );

        const newPrice =
          newSection
            .querySelector(
              `#price-${escapedSectionId}`
            );

        const currentSku =
          currentSection.querySelector(
            '.product-sku'
          );

        const newSku =
          newSection.querySelector(
            '.product-sku'
          );

        /*
         * Replace the complete sticky Add to Cart component.
         */
        const currentSticky =
          currentSection
            .querySelector(
              'product-add-to-cart-sticky'
            );

        const newSticky =
          newSection
            .querySelector(
              'product-add-to-cart-sticky'
            );

        /*
        * Update the displayed sibling product SKU.
        */
        if (currentSku && newSku) {
          currentSku.textContent =
            newSku.textContent.trim();
        } else if (currentSku) {
          currentSku.textContent = '';
        }            

        /*
         * Replace the requested PDP components.
         */
        currentImageContainer
          .replaceWith(
            newImageContainer
          );

        replaceOptionalElement(
          currentSiblings,
          newSiblings
        );

        replaceOptionalElement(
          currentSticky,
          newSticky
        );

        currentVariantSelects
          .replaceWith(
            newVariantSelects
          );

        replaceOptionalElement(
          currentPrice,
          newPrice
        );

        updateFinalSaleMessage(
          currentSection,
          destinationProduct
        );

        updateProductImageCountClass(
          currentSection,
          destinationProduct
        );

        /*
         * Use the theme's existing recommendation loader.
         */
        try {
          updateProductRecommendations({
            currentSection,
            newSection,
            productId:
              destinationProduct.id
          });
        } catch (error) {
          console.warn(
            'Could not update product recommendations.',
            error
          );
        }

        /*
         * Allow newly inserted custom elements to connect.
         */
        await waitForDOM();

        reconnectVariantSelectors(
          sectionId
        );

        updateProductFormVariant(
          sectionId,
          destinationUrl
        );

        /*
         * Do not finish an older request after a newer click.
         */
        if (
          activeRequestController !==
          requestController
        ) {
          return;
        }

        newImageContainer
          .scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

        const cleanUrl =
          new URL(
            destinationUrl,
            window.location.origin
          );

        cleanUrl
          .searchParams
          .delete(
            'section_id'
          );

        if (
          addHistoryEntry
        ) {
          window.history.pushState(
            {
              siblingProductAjax:
                true,
              url:
                cleanUrl.toString()
            },
            '',
            cleanUrl.toString()
          );
        }

        const pageTitle = newGrid.dataset.pageTitle?.trim();
        const shopName = newGrid.dataset.shopName?.trim();
        const destinationVariantId = cleanUrl.searchParams.get('variant');

        const destinationVariant =
          destinationProduct
            .variants
            ?.find(
              (variant) =>
                String(variant.id) ===
                String(
                  destinationVariantId
                )
            ) ||
          destinationProduct
            .variants?.[0];

        const productColor =
          destinationVariant
            ?.option1
            ?.trim();

        if (pageTitle) {
          let customPageTitle =
            pageTitle;

          if (
            productColor &&
            productColor !==
              'Default Title'
          ) {
            /*
            * Match:
            * product_color |
            * downcase |
            * capitalize each word
            */
            const formattedColor =
              productColor
                .toLowerCase()
                .split(/\s+/)
                .map(
                  (word) =>
                    word.charAt(0)
                      .toUpperCase() +
                    word.slice(1)
                )
                .join(' ');

            customPageTitle +=
              ` - ${formattedColor}`;
          }

          if (
            shopName &&
            !customPageTitle.includes(
              shopName
            )
          ) {
            customPageTitle +=
              ` | ${shopName}`;
          }

          document.title =
            customPageTitle;
        }

        const activeGrid =
          currentSection
            .querySelector(
              '.product-grid-container'
            );

        document.dispatchEvent(
          new CustomEvent(
            'product:sibling-loaded',
            {
              detail: {
                url:
                  cleanUrl.toString(),
                sectionId,
                container:
                  activeGrid
              }
            }
          )
        );
      } catch (error) {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        console.warn(
          'Could not AJAX-load the sibling product.',
          error
        );

        window.location.assign(
          destinationUrl
        );
      } finally {
        if (
          activeRequestController ===
          requestController
        ) {
          activeRequestController =
            null;

          currentSection
            .classList.remove(
              'is-loading'
            );

          currentSection
            .removeAttribute(
              'aria-busy'
            );

          document.body
            .classList.remove(
              'sibling-product-loading'
            );
        }
      }
    };

  /*
   * Match selected options and AJAX-load the sibling on either
   * the full PDP or inside the side Quick View.
   */
  document.addEventListener(
    'click',
    async (event) => {
      const link =
        event.target.closest?.(
          '[data-sibling-product-link]'
        );

      if (
        !link ||
        event.button !== 0 ||
        event.defaultPrevented
      ) {
        return;
      }

      /*
       * Preserve new-tab and modified-click behavior.
       */
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();

      const fallbackUrl =
        link.dataset
          .siblingProductUrl ||
        link.href;

      const quickViewProduct =
        link.closest(
          '.thb-product-detail--quick-view'
        );

      const productDrawer =
        link.closest(
          '#Product-Drawer'
        );

      const isQuickView =
        Boolean(
          quickViewProduct &&
          productDrawer
        );

      /*
       * Quick View has no outer shopify-section element,
       * so its section ID comes from its variant selector.
       */
      const quickViewVariantSelector =
        quickViewProduct
          ?.querySelector(
            'variant-selects[data-section]'
          );

      const currentSection =
        isQuickView
          ? null
          : getMainProductSection(
              link
            );

      const sectionId =
        isQuickView
          ? quickViewVariantSelector
              ?.dataset.section
          : getSectionId(
              currentSection
            );

      if (!sectionId) {
        window.location.assign(
          fallbackUrl
        );

        return;
      }

      try {
        const destination =
          await buildSiblingDestination(
            link,
            sectionId
          );

        if (isQuickView) {
          await loadQuickViewSibling({
            destinationUrl:
              destination.url,
            currentDrawer:
              productDrawer,
            sectionId
          });

          return;
        }

        await loadSiblingProduct({
          destinationUrl:
            destination.url,
          siblingProduct:
            destination.product,
          currentSection,
          addHistoryEntry: true
        });
      } catch (error) {
        console.warn(
          'Could not determine the sibling variant.',
          error
        );

        window.location.assign(
          fallbackUrl
        );
      }
    }
  );

  /*
   * AJAX-load the correct product when Back or Forward is used.
   */
  window.addEventListener(
    'popstate',
    () => {
      const currentSection =
        document.querySelector(
          '[id^="shopify-section-"]' +
          ':has(.product-grid-container)'
        );

      if (!currentSection) {
        window.location.reload();
        return;
      }

      loadSiblingProduct({
        destinationUrl:
          window.location.href,
        currentSection,
        addHistoryEntry: false
      });
    }
  );
})();

(() => {
  const containerSelector =
    '.product-card-sibling-swatches';

  const swatchSelector =
    '.product-card-sibling-swatch' +
    ':not(.swatch-overflow-toggle)';

  const hiddenClass =
    'swatch-row-hidden';

  const toggleClass =
    'swatch-overflow-toggle';

  let resizeTimer;
  let updateQueued = false;

  function getSwatches(container) {
    return Array.from(
      container.querySelectorAll(
        swatchSelector
      )
    );
  }

  function resetContainer(container) {
    container
      .querySelector(
        `.${toggleClass}`
      )
      ?.remove();

    getSwatches(container).forEach(
      (swatch) => {
        swatch.classList.remove(
          hiddenClass
        );
      }
    );
  }

  function createOverflowButton(
    hiddenCount
  ) {
    const button =
      document.createElement('button');

    button.type = 'button';

    button.className =
      `product-card-sibling-swatch ${toggleClass}`;

    button.setAttribute(
      'aria-label',
      `Show ${hiddenCount} more colors`
    );

    button.setAttribute(
      'aria-expanded',
      'false'
    );

    button.innerHTML = `
      <span class="swatch-overflow-count">+${hiddenCount}</span>
    `;

    return button;
  }

  function collapseSwatches(
    container
  ) {
    if (
      container.dataset
        .swatchesExpanded === 'true'
    ) {
      resetContainer(container);
      return;
    }

    resetContainer(container);

    const swatches =
      getSwatches(container);

    if (
      swatches.length < 2 ||
      container.offsetWidth === 0
    ) {
      return;
    }

    const firstRowTop =
      swatches[0].offsetTop;

    let firstRowCount = 0;

    for (const swatch of swatches) {
      if (
        Math.abs(
          swatch.offsetTop -
          firstRowTop
        ) <= 1
      ) {
        firstRowCount++;
      } else {
        break;
      }
    }

    if (
      firstRowCount ===
      swatches.length
    ) {
      return;
    }

    const visibleSwatchCount =
      Math.max(
        0,
        firstRowCount - 1
      );

    const hiddenCount =
      swatches.length -
      visibleSwatchCount;

    swatches.forEach(
      (swatch, index) => {
        swatch.classList.toggle(
          hiddenClass,
          index >=
            visibleSwatchCount
        );
      }
    );

    container.appendChild(
      createOverflowButton(
        hiddenCount
      )
    );
  }

  function updateAllSwatches() {
    document
      .querySelectorAll(
        containerSelector
      )
      .forEach((container) => {
        collapseSwatches(
          container
        );
      });
  }

  function queueUpdate() {
    if (updateQueued) {
      return;
    }

    updateQueued = true;

    requestAnimationFrame(() => {
      updateAllSwatches();
      updateQueued = false;
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const toggle =
        event.target.closest(
          `.${toggleClass}`
        );

      if (!toggle) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const container =
        toggle.closest(
          containerSelector
        );

      if (!container) {
        return;
      }

      container.dataset
        .swatchesExpanded = 'true';

      resetContainer(container);
    },
    true
  );

  window.addEventListener(
    'resize',
    () => {
      clearTimeout(
        resizeTimer
      );

      resizeTimer =
        setTimeout(
          queueUpdate,
          150
        );
    },
    {
      passive: true
    }
  );

  document.addEventListener(
    'shopify:section:load',
    queueUpdate
  );

  const observer =
    new MutationObserver(
      (mutations) => {
        const hasNewSwatches =
          mutations.some(
            (mutation) =>
              Array.from(
                mutation.addedNodes
              ).some((node) => {
                if (
                  !(
                    node instanceof
                    Element
                  )
                ) {
                  return false;
                }

                if (
                  node.classList.contains(
                    toggleClass
                  )
                ) {
                  return false;
                }

                return (
                  node.matches(
                    containerSelector
                  ) ||
                  node.querySelector(
                    containerSelector
                  )
                );
              })
          );

        if (hasNewSwatches) {
          queueUpdate();
        }
      }
    );

  function initialize() {
    queueUpdate();

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }
})();